import { useEffect, useState } from 'react';
import { manualCharge } from '../services/walletService';

const fmt = (n) => (n ?? 0).toLocaleString();

function ManualChargeModal({ isOpen, userId, userDetail, onClose, onSuccess }) {
  const [pointType, setPointType] = useState('CP');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [restrictedReason, setRestrictedReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setPointType('CP'); setAmount(''); setReason('');
      setRestrictedReason(''); setSubmitting(false); setError('');
    }
  }, [isOpen]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && isOpen && !submitting) onClose(); };
    if (isOpen) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, submitting]);

  if (!isOpen) return null;

  const isRestricted = userDetail?.riskStatus === 'RESTRICTED';
  const currentBalance = pointType === 'CP' ? (userDetail?.cpBalance ?? 0) : (userDetail?.epBalance ?? 0);
  const parsedAmount = Number(amount.replace(/,/g, '')) || 0;
  const expectedBalance = parsedAmount > 0 ? currentBalance + parsedAmount : null;

  const errMsg = (err) =>
    err?.response?.data?.data?.detail || err?.response?.data?.detail || err?.message || '처리 중 오류가 발생했습니다.';

  async function handleSubmit() {
    setError('');
    if (!parsedAmount || parsedAmount <= 0) { setError('충전 금액은 0보다 커야 합니다.'); return; }
    if (!reason.trim()) { setError('지급 사유를 입력해주세요.'); return; }
    if (isRestricted && !restrictedReason.trim()) { setError('처리 사유를 입력해주세요.'); return; }

    setSubmitting(true);
    try {
      await manualCharge(userId, { pointType, amount: parsedAmount, reason, restrictedReason: isRestricted ? restrictedReason : undefined }, crypto.randomUUID());
      onSuccess(); onClose();
    } catch (err) {
      setError(errMsg(err));
    } finally { setSubmitting(false); }
  }

  const label = (text, required) => (
    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#6b7280', marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {text}{required && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
    </label>
  );

  const inputBase = {
    width: '100%', padding: '10px 14px', border: '1px solid #e5e7eb',
    borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
    fontFamily: 'inherit', outline: 'none', color: '#111827',
  };

  const btn = (style) => ({
    height: '40px', padding: '0 20px', fontSize: '14px', fontWeight: 500,
    borderRadius: '8px', cursor: submitting ? 'not-allowed' : 'pointer', border: 'none', ...style,
  });

  return (
    <div className="modal-overlay" data-testid="manual-charge-modal-overlay" onClick={() => { if (!submitting) onClose(); }}>
      <div
        className="modal-content" data-testid="manual-charge-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '460px', width: '92%', padding: 0, borderRadius: '16px', overflow: 'hidden', boxShadow: '0 24px 48px rgba(0,0,0,0.12)' }}
      >
        {/* 헤더 */}
        <div style={{ padding: '20px 22px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '17px', fontWeight: 700, color: '#0a0a0a', letterSpacing: '-0.3px' }}>수기 충전</span>
          <button className="modal-close-btn" onClick={() => { if (!submitting) onClose(); }} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#aaa', lineHeight: 1, padding: '2px 4px' }}>✕</button>
        </div>

        <div style={{ padding: '22px 22px 16px' }}>
          {/* RESTRICTED 경고 */}
          {isRestricted && (
            <div data-testid="restricted-banner" style={{ marginBottom: '18px', padding: '11px 14px', background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: '8px', color: '#be123c', fontSize: '13px', fontWeight: 600 }}>
              ⚠️ 위험 사용자 — 충전 시 처리 사유 필수 입력
            </div>
          )}

          {/* 포인트 유형 */}
          <div style={{ marginBottom: '18px' }}>
            {label('포인트 유형', true)}
            <div style={{ display: 'flex', gap: '10px' }}>
              {['CP', 'EP'].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setPointType(type)}
                  data-testid={`radio-${type.toLowerCase()}`}
                  style={{
                    flex: 1, height: '40px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                    border: pointType === type ? 'none' : '1.5px solid #e5e7eb',
                    background: pointType === type ? '#6366f1' : 'white',
                    color: pointType === type ? 'white' : '#6b7280',
                  }}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* 현재 잔액 / 예상 잔액 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '18px', padding: '14px 16px', background: '#f9fafb', borderRadius: '10px' }}>
            <div>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '6px', fontWeight: 500 }}>현재 {pointType} 잔액</div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#111827' }} data-testid="current-balance">{fmt(currentBalance)} P</div>
            </div>
            <div>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '6px', fontWeight: 500 }}>충전 후 예상 잔액</div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: expectedBalance != null ? '#16a34a' : '#d1d5db' }}>
                {expectedBalance != null ? `${fmt(expectedBalance)} P` : '-'}
              </div>
            </div>
          </div>

          {/* 충전 금액 */}
          <div style={{ marginBottom: '16px' }}>
            {label('충전 금액', true)}
            <input
              type="text"
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="충전할 금액을 입력하세요"
              data-testid="amount-input"
              style={inputBase}
            />
          </div>

          {/* 지급 사유 */}
          <div style={{ marginBottom: '16px' }}>
            {label('지급 사유', true)}
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="지급 사유를 입력해주세요"
              rows={3}
              data-testid="reason-input"
              style={{ ...inputBase, resize: 'none', lineHeight: 1.6 }}
            />
          </div>

          {/* RESTRICTED 처리 사유 */}
          {isRestricted && (
            <div style={{ marginBottom: '16px' }}>
              {label('처리 사유', true)}
              <textarea
                value={restrictedReason}
                onChange={(e) => setRestrictedReason(e.target.value)}
                placeholder="처리 사유를 입력해주세요"
                rows={2}
                data-testid="restricted-reason-input"
                style={{ ...inputBase, resize: 'none', lineHeight: 1.6, borderColor: isRestricted && !restrictedReason.trim() ? '#fca5a5' : '#e5e7eb' }}
              />
            </div>
          )}

          {error && (
            <div data-testid="error-message" style={{ padding: '10px 14px', background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: '8px', color: '#be123c', fontSize: '13px', fontWeight: 500 }}>
              {error}
            </div>
          )}
        </div>

        {/* 하단 버튼 */}
        <div style={{ padding: '12px 22px 18px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={() => { if (!submitting) onClose(); }} disabled={submitting} data-testid="btn-cancel"
            style={btn({ background: '#f4f4f5', color: '#52525b' })}>
            취소
          </button>
          <button onClick={handleSubmit} disabled={submitting} data-testid="btn-submit"
            style={btn({ background: submitting ? '#e5e7eb' : '#6366f1', color: submitting ? '#9ca3af' : 'white', fontWeight: 600 })}>
            {submitting ? '처리 중...' : '충전 확정'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ManualChargeModal;
